import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Banknote, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { invoiceApi } from '../../lib/api';

const fmt = (v) => `${(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/**
 * Einheitlicher Dialog "Zahlung erfassen" – wird sowohl in der Buchhaltung
 * (InvoicesPage) als auch auf der Teilnehmer-Detailseite (Fortbildungen →
 * Seminar → Teilnehmer) verwendet. Layout, Felder und Texte sind 1:1 identisch.
 *
 * Props:
 *  - invoice (object): Quittungs-Datensatz – muss id, amount, paid_amount enthalten.
 *  - open / onOpenChange: Standard-Dialog-Steuerung
 *  - onSuccess: Callback nach erfolgreichem Speichern (z. B. zum Reload)
 *  - participantName, seminarTitle (optional): falls gesetzt, werden sie als
 *    Kontextzeile unter dem Dialog-Titel angezeigt – damit klar bleibt,
 *    für wen/welches Seminar die Zahlung erfasst wird.
 */
export default function PaymentDialog({
    open,
    onOpenChange,
    invoice,
    onSuccess,
    participantName,
    seminarTitle,
}) {
    const today = new Date().toISOString().split('T')[0];
    const remaining = invoice ? Math.max(0, (invoice.amount || 0) - (invoice.paid_amount || 0)) : 0;
    const [form, setForm] = useState({ paid_amount: remaining, payment_date: today, payment_reference: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && invoice) {
            setForm({
                paid_amount: Math.max(0, (invoice.amount || 0) - (invoice.paid_amount || 0)),
                payment_date: today,
                payment_reference: '',
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, invoice?.id]);

    const submit = async () => {
        if (!form.paid_amount || form.paid_amount <= 0) {
            toast.error('Bitte einen gültigen Betrag eingeben');
            return;
        }
        if (!form.payment_date) {
            toast.error('Bitte ein Datum eingeben');
            return;
        }
        setSaving(true);
        try {
            const res = await invoiceApi.markPaid(invoice.id, form);
            const updated = res?.data || {};
            const receiptNo = updated.receipt_number || invoice.receipt_number;
            const fullyPaid = updated.status === 'paid';

            if (fullyPaid && invoice.is_in_house_collective) {
                toast.success(`Zahlung erfasst – Sammelrechnung ${invoice.invoice_number || ''} als bezahlt markiert`.trim());
            } else if (fullyPaid) {
                toast.success(
                    receiptNo ? `Quittung ${receiptNo} wurde versendet` : 'Quittung wurde versendet',
                    {
                        description: 'PDF in Dropbox archiviert · E-Mail an das Mitglied verschickt',
                        duration: 8000,
                        action: {
                            label: 'Quittung anzeigen',
                            onClick: async () => {
                                try {
                                    const pdf = await invoiceApi.downloadPdf(invoice.id);
                                    const url = URL.createObjectURL(new Blob([pdf.data], { type: 'application/pdf' }));
                                    window.open(url, '_blank', 'noopener,noreferrer');
                                    // Blob-URL nach kurzer Zeit aufräumen
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                } catch {
                                    toast.error('Quittung konnte nicht geladen werden');
                                }
                            },
                        },
                    },
                );
            } else {
                toast.success('Teilzahlung erfasst');
            }
            onOpenChange(false);
            onSuccess?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="payment-dialog">
                <DialogHeader>
                    <DialogTitle>Zahlung erfassen</DialogTitle>
                    {(participantName || seminarTitle) && (
                        <p className="text-xs text-muted-foreground" data-testid="payment-dialog-context">
                            {[participantName, seminarTitle].filter(Boolean).join(' · ')}
                        </p>
                    )}
                </DialogHeader>
                {invoice && (
                    <div className="space-y-4 py-2">
                        <div className="rounded-lg bg-muted/40 p-3 text-sm space-y-1">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Gesamt:</span>
                                <span>{fmt(invoice.amount)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Bereits bezahlt:</span>
                                <span>{fmt(invoice.paid_amount || 0)}</span>
                            </div>
                            <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                                <span>Offen:</span>
                                <span>{fmt(remaining)}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Betrag *</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.paid_amount}
                                    onChange={e => setForm(f => ({ ...f, paid_amount: parseFloat(e.target.value) || 0 }))}
                                    data-testid="payment-amount"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Datum *</Label>
                                <Input
                                    type="date"
                                    value={form.payment_date}
                                    onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))}
                                    data-testid="payment-date"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Zahlungsart</Label>
                            <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm flex items-center gap-2" data-testid="payment-method-fixed">
                                <Banknote className="h-4 w-4 text-muted-foreground" />
                                Überweisung
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Referenz / Verwendungszweck (optional)</Label>
                            <Input
                                value={form.payment_reference}
                                onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))}
                                data-testid="payment-reference"
                            />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
                    <Button onClick={submit} disabled={saving || !form.paid_amount || !form.payment_date} data-testid="payment-submit">
                        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                        Zahlung speichern
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
